import {
  formatEarningsAmount,
  formatEarningsPercentage,
  getEarningsHeadline,
} from '@/metrics/pages/PerformanceInsightsPage/GlobalTab/EarningsSection/helpers'

import {
  earningsBridgeActiveDeductionKeys,
  earningsBridgeColumnKeys,
  earningsBridgeColumnLabels,
  earningsBridgeColumnOrder,
  earningsBridgeCopies,
  earningsBridgeDeductionKeys,
  earningsBridgeFullShare,
} from '../constants'

const getShareOfNetSales = (amount, netSales) => (Number(amount) * earningsBridgeFullShare) / netSales

const getPlaceholderColumn = key => {
  return { key, value: null, offset: 0, height: 0, isPlaceholder: true }
}

const getDeductionSteps = (earnings, netSales, activeDeductionKeys) => {
  return earningsBridgeDeductionKeys.reduce(
    ({ steps, accumulated }, key) => {
      const value = earnings[key]

      if (!activeDeductionKeys.includes(key) || value === null) {
        return { steps: [...steps, { key, value: null }], accumulated }
      }

      const end = accumulated - getShareOfNetSales(value, netSales)

      return { steps: [...steps, { key, value, start: accumulated, end }], accumulated: end }
    },
    { steps: [], accumulated: earningsBridgeFullShare }
  ).steps
}

const getMarginStep = (margin, netSales) => {
  if (margin === null) return { key: earningsBridgeColumnKeys.margin, value: null }

  return { key: earningsBridgeColumnKeys.margin, value: margin, start: 0, end: getShareOfNetSales(margin, netSales) }
}

const getEarningsBridgeSteps = earnings => {
  const netSales = Number(earnings.net_sales)
  const headline = getEarningsHeadline(earnings)

  return [
    { key: earningsBridgeColumnKeys.netSales, value: earnings.net_sales, start: 0, end: earningsBridgeFullShare },
    ...getDeductionSteps(earnings, netSales, earningsBridgeActiveDeductionKeys[headline.level]),
    getMarginStep(headline.amount, netSales),
  ]
}

const mapEarningsBridgeColumn = (step, scaleMin, scaleRange) => {
  if (step.value === null) return getPlaceholderColumn(step.key)

  return {
    key: step.key,
    value: step.value,
    offset: ((Math.min(step.start, step.end) - scaleMin) * earningsBridgeFullShare) / scaleRange,
    height: (Math.abs(step.end - step.start) * earningsBridgeFullShare) / scaleRange,
    isPlaceholder: false,
  }
}

export const getEarningsBridgeColumns = earnings => {
  if (earnings.net_sales === null || Number(earnings.net_sales) === 0) {
    return earningsBridgeColumnOrder.map(key => getPlaceholderColumn(key))
  }

  const steps = getEarningsBridgeSteps(earnings)
  const bounds = steps.filter(step => step.value !== null).flatMap(step => [step.start, step.end])
  const scaleMin = Math.min(...bounds)
  const scaleMax = Math.max(...bounds)

  return steps.map(step => mapEarningsBridgeColumn(step, scaleMin, scaleMax - scaleMin))
}

const getEarningsBridgeColumnLabel = (key, level) => {
  if (key === earningsBridgeColumnKeys.margin) return earningsBridgeCopies[level].marginLabel

  return earningsBridgeColumnLabels[key]
}

const formatEarningsBridgeColumnAmount = (column, isDeduction, currency, language) => {
  if (!isDeduction || column.isPlaceholder) return formatEarningsAmount(column.value, currency, language)

  return formatEarningsAmount(-Number(column.value), currency, language)
}

export const getEarningsBridgeColumnItems = (earnings, language) => {
  const { level } = getEarningsHeadline(earnings)

  return getEarningsBridgeColumns(earnings).map(column => {
    const isDeduction = earningsBridgeDeductionKeys.includes(column.key)

    return {
      ...column,
      label: getEarningsBridgeColumnLabel(column.key, level),
      amount: formatEarningsBridgeColumnAmount(column, isDeduction, earnings.currency, language),
      isDeduction,
      isNegative: !column.isPlaceholder && Number(column.value) < 0,
    }
  })
}

export const getEarningsBridgeHeadlineValues = (earnings, language) => {
  const headline = getEarningsHeadline(earnings)

  return {
    netSales: formatEarningsAmount(earnings.net_sales, earnings.currency, language),
    margin: formatEarningsAmount(headline.amount, earnings.currency, language),
    percentage: formatEarningsPercentage(headline.percentage, language),
  }
}
